import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getTraitsListingAPI } from "../../redux/apis/traits";

const RuleOptionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    style={{ marginRight: 4, flexShrink: 0 }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.2978 7.50022C12.2714 10.3996 9.89959 12.7286 7.00022 12.7022C4.10084 12.6758 1.77185 10.3039 1.79826 7.40457L1.84608 2.15479L12.3456 2.25044L12.2978 7.50022ZM7.00891 11.7477C9.38113 11.7693 11.3217 9.86374 11.3433 7.49152L11.3824 3.19625L2.79189 3.11799L2.75277 7.41327C2.73116 9.78548 4.6367 11.7261 7.00891 11.7477Z"
      fill="#475CC7"
    />
  </svg>
);

/**
 * TraitList fetches traits from product/traits/main/listing and displays them.
 * On select, calls changeHandler with the selected trait object.
 */
export default function TraitList({
  changeHandler = () => {},
  inputValue = null,
  setAnchorEl,
  platformType = "hemp",
}) {
  const [filter, setFilter] = useState({
    skip: 0,
    limit: 20,
    searchTerm: "",
    userType: "admin",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [traitsList, setTraitsList] = useState([]);
  const observer = useRef();

  const fetchTraits = useCallback(
    (skip = 0, searchTerm = "", append = false) => {
      setIsLoading(true);
      getTraitsListingAPI({
        skip,
        limit: filter.limit,
        searchTerm: searchTerm || "",
        userType: "admin",
        platformType: platformType || "hemp",
      })
        .then((res) => {
          const data = res?.data ?? res?.traits ?? res;
          const list = Array.isArray(data) ? data : [];
          const totalCount = res?.total ?? list.length;
          if (append) {
            setTraitsList((prev) => [...prev, ...list]);
          } else {
            setTraitsList(list);
          }
          setTotal(totalCount);
        })
        .catch(() => {
          setTraitsList([]);
          setTotal(0);
        })
        .finally(() => setIsLoading(false));
    },
    [filter.limit, platformType]
  );

  useEffect(() => {
    fetchTraits(0, filter.searchTerm, false);
  }, [filter.searchTerm]);

  const lastPostElementRef = useCallback(
    (node) => {
      if (isLoading || traitsList.length >= total) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && traitsList.length < total) {
            const newSkip = filter.skip + filter.limit;
            setFilter((prev) => ({ ...prev, skip: newSkip }));
            fetchTraits(newSkip, filter.searchTerm, true);
          }
        },
        { root: null, rootMargin: "100px", threshold: 0.1 }
      );
      if (node) observer.current.observe(node);
    },
    [isLoading, traitsList.length, total, filter.skip, filter.limit, filter.searchTerm, fetchTraits]
  );

  useEffect(() => {
    fetchTraits(0, "", false);
  }, []);

  const isSelected = (trait) => {
    if (inputValue == null) return false;
    const id = inputValue?._id ?? inputValue?.id;
    return (trait?._id ?? trait?.id) === id;
  };

  return (
    <Box sx={{ minWidth: 220 }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          fullWidth
          size="small"
          variant="standard"
          placeholder="Search traits..."
          onChange={debounce((e) => {
            const searchValue = e.target.value;
            setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));
          }, 400)}
        />
      </Box>
      {!isLoading && traitsList.length === 0 && (
        <Box sx={{ p: 2, transform: "scale(0.9)" }}>No traits found</Box>
      )}
      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {traitsList.map((trait, index) => {
          const selected = isSelected(trait);
          const isLast = index === traitsList.length - 1;
          const name = trait?.mainTraitName ?? trait?.traitName ?? trait?.name ?? trait?.slug ?? "-";
          return (
            <MenuItem
              key={trait?._id ?? trait?.id ?? index}
              selected={selected}
              ref={isLast ? lastPostElementRef : null}
              onClick={() => {
                if (typeof setAnchorEl === "function") setAnchorEl(null);
                changeHandler({
                  ruleValue: selected ? null : trait,
                });
              }}
              sx={{ py: 1 }}
            >
              <RuleOptionIcon />
              <Typography
                sx={{
                  paddingLeft: 1,
                  color: "",
                  fontWeight: selected ? 600 : 400,
                }}
              >
                {name}
              </Typography>
            </MenuItem>
          );
        })}
      </Box>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} color="primary" />
        </Box>
      )}
    </Box>
  );
}
